import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";
import { toast } from "sonner";
import {
  emailTemplateDetailEndpoint,
  emailTemplateRenderEndpoint,
  emailTemplateTestSendEndpoint,
  emailTemplatesEndpoint,
  emailTemplatesSeedDefaultsEndpoint,
} from "@/config/endpoints";
import type { DesignDocument, EmailTemplate } from "../types/gmail.types";

export const EMAIL_TEMPLATES_QUERY_KEY = "email-templates";

function adapt(raw: any): EmailTemplate {
  return {
    id: raw.id,
    name: raw.name,
    slug: raw.slug,
    category: raw.category ?? "general",
    subject: raw.subject ?? "",
    htmlBody: raw.htmlBody ?? "",
    plainBody: raw.plainBody ?? null,
    designJson: raw.designJson ?? null,
    variables: raw.variables ?? [],
    vertical: raw.vertical ?? null,
    isShared: Boolean(raw.isShared),
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
  };
}

export function useEmailTemplates(params?: {
  category?: string;
  vertical?: number;
  isShared?: boolean;
  q?: string;
}) {
  const query = useQuery({
    queryKey: [EMAIL_TEMPLATES_QUERY_KEY, params ?? {}],
    queryFn: async (): Promise<EmailTemplate[]> => {
      const res = await axiosInstance.get(emailTemplatesEndpoint, { params });
      const arr: unknown[] = res.data?.data ?? [];
      return arr.map(adapt);
    },
    staleTime: 1000 * 60 * 2,
  });
  return {
    templates: (query.data ?? []) as EmailTemplate[],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useEmailTemplate(id: number | null) {
  const query = useQuery({
    queryKey: [EMAIL_TEMPLATES_QUERY_KEY, "detail", id],
    queryFn: async () => {
      const url = emailTemplateDetailEndpoint.replace(":id", String(id));
      const res = await axiosInstance.get(url);
      return adapt(res.data?.data);
    },
    enabled: Boolean(id),
    staleTime: 1000 * 60,
  });
  return {
    template: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
  };
}

export interface SaveTemplatePayload {
  name: string;
  slug?: string;
  category?: string;
  subject: string;
  htmlBody: string;
  plainBody?: string;
  designJson?: DesignDocument | null;
  variables?: string[];
  isShared?: boolean;
  vertical?: number | null;
}

export function useCreateEmailTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SaveTemplatePayload) => {
      const res = await axiosInstance.post(emailTemplatesEndpoint, payload);
      return adapt(res.data?.data);
    },
    onSuccess: () => {
      toast.success("Template saved");
      qc.invalidateQueries({ queryKey: [EMAIL_TEMPLATES_QUERY_KEY] });
    },
    onError: (err: any) =>
      toast.error(
        err?.response?.data?.message || "Failed to save template",
      ),
  });
}

export function useUpdateEmailTemplate(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<SaveTemplatePayload>) => {
      const url = emailTemplateDetailEndpoint.replace(":id", String(id));
      const res = await axiosInstance.patch(url, payload);
      return adapt(res.data?.data);
    },
    onSuccess: () => {
      toast.success("Template updated");
      qc.invalidateQueries({ queryKey: [EMAIL_TEMPLATES_QUERY_KEY] });
    },
    onError: (err: any) =>
      toast.error(
        err?.response?.data?.message || "Failed to update template",
      ),
  });
}

export function useDeleteEmailTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const url = emailTemplateDetailEndpoint.replace(":id", String(id));
      await axiosInstance.delete(url);
      return id;
    },
    onSuccess: () => {
      toast.success("Template deleted");
      qc.invalidateQueries({ queryKey: [EMAIL_TEMPLATES_QUERY_KEY] });
    },
    onError: (err: any) =>
      toast.error(
        err?.response?.data?.message || "Failed to delete template",
      ),
  });
}

export interface RenderResult {
  subject: string;
  html: string;
  plain: string;
  missingVariables: string[];
}

export function useRenderTemplate() {
  return useMutation({
    mutationFn: async ({
      id,
      context,
    }: {
      id: number;
      context: Record<string, unknown>;
    }) => {
      const url = emailTemplateRenderEndpoint.replace(":id", String(id));
      const res = await axiosInstance.post(url, { context });
      return res.data?.data as RenderResult;
    },
  });
}

export interface SeedDefaultsResult {
  /** Templates created in this run (0 = all defaults already existed). */
  created: number;
  /** Total live templates the tenant has after the seed. */
  totalAfter: number;
  /** Industry the tenant resolves to (for diagnostics). */
  industryId: number | null;
  /** True when the tenant's industry has no vertical-specific seed. */
  industryHasNoSeed: boolean;
}

/**
 * One-shot mutation to hydrate the tenant with our starter email
 * templates. Idempotent server-side, so repeated clicks are safe and
 * simply add any missing slugs. Surface this on the empty state.
 */
export function useSeedDefaultTemplates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<SeedDefaultsResult> => {
      const res = await axiosInstance.post(emailTemplatesSeedDefaultsEndpoint);
      return res.data?.data as SeedDefaultsResult;
    },
    onSuccess: (data) => {
      if (data.created > 0) {
        toast.success(
          data.created === 1
            ? "1 sample template added"
            : `${data.created} sample templates added`,
        );
      } else if (data.totalAfter === 0 && data.industryHasNoSeed) {
        toast.info(
          "No starter templates available for your industry yet — create one to get going.",
        );
      } else {
        toast.info("Sample templates already in your library");
      }
      qc.invalidateQueries({ queryKey: [EMAIL_TEMPLATES_QUERY_KEY] });
    },
    onError: (err: any) =>
      toast.error(
        err?.response?.data?.message || "Failed to add sample templates",
      ),
  });
}

export function useTestSendTemplate() {
  return useMutation({
    mutationFn: async ({
      id,
      to,
      context,
    }: {
      id: number;
      to?: string;
      context?: Record<string, unknown>;
    }) => {
      const url = emailTemplateTestSendEndpoint.replace(":id", String(id));
      const res = await axiosInstance.post(url, { to, context: context ?? {} });
      return res.data?.data;
    },
    onSuccess: () => toast.success("Test email queued"),
    onError: (err: any) =>
      toast.error(
        err?.response?.data?.message || "Failed to send test email",
      ),
  });
}
